// src/components/admin/UserAnalyticsTab.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VerifiedIcon from '@mui/icons-material/Verified';
import BlockIcon from '@mui/icons-material/Block';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArticleIcon from '@mui/icons-material/Article';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import {
  getUserAnalytics,
  getUserGrowth,
  getUserRetention,
  getUserDistribution,
  getTopFollowers,
} from '../../services/adminService';
import {
  UserAnalyticsResponse,
  UserGrowthResponse,
  UserRetentionResponse,
  UserDistributionResponse,
  TopFollowersResponse,
} from '../../types/admin';

const UserAnalyticsTab: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<UserAnalyticsResponse | null>(null);
  const [growthData, setGrowthData] = useState<UserGrowthResponse | null>(null);
  const [retentionData, setRetentionData] = useState<UserRetentionResponse | null>(null);
  const [distributionData, setDistributionData] = useState<UserDistributionResponse | null>(null);
  const [, setTopFollowersData] = useState<TopFollowersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<number>(30);
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');

  useEffect(() => {
    fetchData();
  }, [period, groupBy]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [analyticsRes, growthRes, retentionRes, distributionRes, topFollowersRes] = await Promise.all([
        getUserAnalytics(period),
        getUserGrowth(period, groupBy).catch(() => null),
        getUserRetention().catch(() => null),
        getUserDistribution().catch(() => null),
        getTopFollowers(10).catch(() => null),
      ]);
      setData(analyticsRes);
      setGrowthData(growthRes);
      setRetentionData(retentionRes);
      setDistributionData(distributionRes);
      setTopFollowersData(topFollowersRes);
    } catch (err: any) {
      console.error('Failed to fetch user analytics:', err);
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newPeriod: number | null) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
    }
  };

  const handleGroupByChange = (event: SelectChangeEvent<'day' | 'week' | 'month'>) => {
    setGroupBy(event.target.value as 'day' | 'week' | 'month');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  // Helper function to extract count from value that might be a number or {count, percentage} object
  const getCount = (value: any): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && 'count' in value) return value.count;
    return 0;
  };

  // Format chart data
  const chartData = growthData?.chart_data?.map(item => ({
    date: new Date(item.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
    nouveaux: item.new_users,
    cumul: item.cumulative_total || 0,
  })) || [];

  // Role distribution data for bar chart
  const roleData = Object.entries(data.demographics.by_role).map(([role, value]) => ({
    role: role.charAt(0).toUpperCase() + role.slice(1),
    count: getCount(value),
  }));

  // Language distribution data
  const languageData = Object.entries(data.demographics.by_language).map(([lang, value]) => ({
    language: lang.toUpperCase(),
    count: getCount(value),
  }));

  // Growth rate
  const growthRate = growthData?.summary?.growth_rate ? Number(growthData.summary.growth_rate) : 0;
  const isPositiveGrowth = growthRate >= 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Controls */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1e293b">
            {t('userAnalytics.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Vue d'ensemble des utilisateurs et de leur activité
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('userAnalytics.groupBy')}</InputLabel>
            <Select value={groupBy} label={t('userAnalytics.groupBy')} onChange={handleGroupByChange}>
              <MenuItem value="day">{t('userAnalytics.daily')}</MenuItem>
              <MenuItem value="week">{t('userAnalytics.weekly')}</MenuItem>
              <MenuItem value="month">{t('userAnalytics.monthly')}</MenuItem>
            </Select>
          </FormControl>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid #e2e8f0',
                '&.Mui-selected': { bgcolor: '#2563eb', color: 'white', '&:hover': { bgcolor: '#1d4ed8' } },
              },
            }}
          >
            <ToggleButton value={7}>7j</ToggleButton>
            <ToggleButton value={30}>30j</ToggleButton>
            <ToggleButton value={90}>90j</ToggleButton>
            <ToggleButton value={365}>1an</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {/* Key Metrics Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('userAnalytics.totalUsers')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {data.totals.total_users.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#dbeafe', borderRadius: 2 }}>
                  <PeopleIcon sx={{ color: '#2563eb', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('userAnalytics.newUsers')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#16a34a" sx={{ mt: 0.5 }}>
                    +{growthData?.summary?.total_new_users || data.growth.new_users_period}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                    {isPositiveGrowth ? (
                      <TrendingUpIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                    ) : (
                      <TrendingDownIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                    )}
                    <Typography variant="caption" color={isPositiveGrowth ? '#16a34a' : '#dc2626'} fontWeight={600}>
                      {isPositiveGrowth ? '+' : ''}{growthRate.toFixed(1)}%
                    </Typography>
                  </Stack>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#dcfce7', borderRadius: 2 }}>
                  <PersonAddIcon sx={{ color: '#16a34a', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('userAnalytics.verified')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {data.totals.verified.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {data.totals.verification_rate} du total
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#f3e8ff', borderRadius: 2 }}>
                  <VerifiedIcon sx={{ color: '#7c3aed', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('userAnalytics.banned')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {getCount(distributionData?.by_status?.banned)}
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#fef2f2', borderRadius: 2 }}>
                  <BlockIcon sx={{ color: '#dc2626', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Growth Chart */}
      {chartData.length > 0 && (
        <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
              {t('userAnalytics.growthChart')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Évolution des inscriptions sur la période sélectionnée
            </Typography>
            <Box sx={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorNouveaux" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                    labelStyle={{ fontWeight: 600, color: '#1e293b' }}
                  />
                  <Area type="monotone" dataKey="nouveaux" name="Nouveaux utilisateurs" stroke="#2563eb" strokeWidth={2} fillOpacity={1} fill="url(#colorNouveaux)" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Role Distribution */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('userAnalytics.byRole')}
              </Typography>
              <Box sx={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <BarChart data={roleData} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                    <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis dataKey="role" type="category" tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={false} width={80} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }}
                      formatter={(value: number) => [value.toLocaleString(), 'Utilisateurs']}
                    />
                    <Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Language Distribution */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('userAnalytics.byLanguage')}
              </Typography>
              <Box sx={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <BarChart data={languageData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="language" tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }}
                      formatter={(value: number) => [value.toLocaleString(), 'Utilisateurs']}
                    />
                    <Bar dataKey="count" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Retention */}
        {retentionData?.retention_data && (
          <Grid item xs={12} md={6}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <Box sx={{ p: 1, bgcolor: '#fef3c7', borderRadius: 1.5 }}>
                    <AccessTimeIcon sx={{ color: '#d97706', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={600} color="#1e293b">
                    {t('userAnalytics.retention')}
                  </Typography>
                </Stack>
                <Stack spacing={3}>
                  {retentionData.retention_data.day_1 && (
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight={500} color="#1e293b">{t('userAnalytics.day1Retention')}</Typography>
                        <Typography variant="body2" fontWeight={700} color="#16a34a">{Number(retentionData.retention_data.day_1.rate).toFixed(1)}%</Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(Number(retentionData.retention_data.day_1.rate), 100)}
                        sx={{ height: 10, borderRadius: 1, bgcolor: '#dcfce7', '& .MuiLinearProgress-bar': { bgcolor: '#16a34a', borderRadius: 1 } }}
                      />
                    </Box>
                  )}
                  {retentionData.retention_data.day_7 && (
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight={500} color="#1e293b">{t('userAnalytics.day7Retention')}</Typography>
                        <Typography variant="body2" fontWeight={700} color="#2563eb">{Number(retentionData.retention_data.day_7.rate).toFixed(1)}%</Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(Number(retentionData.retention_data.day_7.rate), 100)}
                        sx={{ height: 10, borderRadius: 1, bgcolor: '#dbeafe', '& .MuiLinearProgress-bar': { bgcolor: '#2563eb', borderRadius: 1 } }}
                      />
                    </Box>
                  )}
                  {retentionData.retention_data.day_30 && (
                    <Box>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight={500} color="#1e293b">{t('userAnalytics.day30Retention')}</Typography>
                        <Typography variant="body2" fontWeight={700} color="#d97706">{Number(retentionData.retention_data.day_30.rate).toFixed(1)}%</Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(Number(retentionData.retention_data.day_30.rate), 100)}
                        sx={{ height: 10, borderRadius: 1, bgcolor: '#fef3c7', '& .MuiLinearProgress-bar': { bgcolor: '#d97706', borderRadius: 1 } }}
                      />
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Activity Metrics */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Box sx={{ p: 1, bgcolor: '#dcfce7', borderRadius: 1.5 }}>
                  <TrendingUpIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {t('userAnalytics.activity')}
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <ArticleIcon sx={{ fontSize: 18, color: '#64748b' }} />
                      <Typography variant="body2" color="text.secondary">{t('userAnalytics.activePosters')}</Typography>
                    </Stack>
                    <Typography variant="h5" fontWeight={700} color="#1e293b">{data.activity.active_posters.toLocaleString()}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <FavoriteIcon sx={{ fontSize: 18, color: '#64748b' }} />
                      <Typography variant="body2" color="text.secondary">{t('userAnalytics.activeCommenters')}</Typography>
                    </Stack>
                    <Typography variant="h5" fontWeight={700} color="#1e293b">{data.activity.active_commenters.toLocaleString()}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: '#dbeafe', borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={500} color="#1e293b">{t('userAnalytics.activityRate')}</Typography>
                      <Typography variant="h5" fontWeight={700} color="#2563eb">{data.activity.activity_rate}</Typography>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* User Status Distribution */}
        {distributionData?.by_status && (
          <Grid item xs={12} md={6}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                  {t('userAnalytics.byStatus')}
                </Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 12, height: 12, bgcolor: '#16a34a', borderRadius: 0.5 }} />
                        <Typography variant="body2" color="#1e293b">{t('userAnalytics.verified')}</Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={600}>{getCount(distributionData.by_status.verified)}</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={data.totals.total_users > 0 ? (getCount(distributionData.by_status.verified) / data.totals.total_users) * 100 : 0}
                      sx={{ height: 8, borderRadius: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#16a34a' } }}
                    />
                  </Box>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 12, height: 12, bgcolor: '#f59e0b', borderRadius: 0.5 }} />
                        <Typography variant="body2" color="#1e293b">{t('userAnalytics.unverified')}</Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={600}>{getCount(distributionData.by_status.unverified)}</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={data.totals.total_users > 0 ? (getCount(distributionData.by_status.unverified) / data.totals.total_users) * 100 : 0}
                      sx={{ height: 8, borderRadius: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#f59e0b' } }}
                    />
                  </Box>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 12, height: 12, bgcolor: '#dc2626', borderRadius: 0.5 }} />
                        <Typography variant="body2" color="#1e293b">{t('userAnalytics.banned')}</Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={600}>{getCount(distributionData.by_status.banned)}</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={data.totals.total_users > 0 ? (getCount(distributionData.by_status.banned) / data.totals.total_users) * 100 : 0}
                      sx={{ height: 8, borderRadius: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#dc2626' } }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Activity Distribution */}
        {distributionData?.by_activity && (
          <Grid item xs={12} md={6}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                  {t('userAnalytics.byActivityLevel')}
                </Typography>
                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="#1e293b">{t('userAnalytics.activeLast7Days')}</Typography>
                      <Typography variant="body2" fontWeight={600} color="#16a34a">{getCount(distributionData.by_activity.active_last_7_days)}</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={data.totals.total_users > 0 ? (getCount(distributionData.by_activity.active_last_7_days) / data.totals.total_users) * 100 : 0}
                      sx={{ height: 8, borderRadius: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#16a34a' } }}
                    />
                  </Box>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="#1e293b">{t('userAnalytics.activeLast30Days')}</Typography>
                      <Typography variant="body2" fontWeight={600} color="#2563eb">{getCount(distributionData.by_activity.active_last_30_days)}</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={data.totals.total_users > 0 ? (getCount(distributionData.by_activity.active_last_30_days) / data.totals.total_users) * 100 : 0}
                      sx={{ height: 8, borderRadius: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#2563eb' } }}
                    />
                  </Box>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="#1e293b">{t('userAnalytics.inactive')}</Typography>
                      <Typography variant="body2" fontWeight={600} color="#94a3b8">{getCount(distributionData.by_activity.inactive)}</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={data.totals.total_users > 0 ? (getCount(distributionData.by_activity.inactive) / data.totals.total_users) * 100 : 0}
                      sx={{ height: 8, borderRadius: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: '#94a3b8' } }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Top Posters */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <Box sx={{ p: 1, bgcolor: '#fef3c7', borderRadius: 1.5 }}>
                  <EmojiEventsIcon sx={{ color: '#d97706', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {t('userAnalytics.topPosters')}
                </Typography>
              </Stack>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('userAnalytics.name')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('userAnalytics.posts')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.top_posters.map((user, index) => (
                      <TableRow key={user.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell sx={{ py: 1.5 }}>
                          {index < 3 ? (
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: index === 0 ? '#fef3c7' : index === 1 ? '#f1f5f9' : '#fed7aa',
                                color: index === 0 ? '#d97706' : index === 1 ? '#64748b' : '#c2410c',
                                fontWeight: 700,
                                fontSize: 12,
                                borderRadius: 1,
                              }}
                            >
                              {index + 1}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">{index + 1}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#2563eb', fontSize: 12 }}>
                              {user.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography variant="body2" fontWeight={600} color="#2563eb">{user.post_count}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Followed */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <Box sx={{ p: 1, bgcolor: '#f3e8ff', borderRadius: 1.5 }}>
                  <PeopleIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {t('userAnalytics.topFollowed')}
                </Typography>
              </Stack>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('userAnalytics.name')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'white', borderBottom: '2px solid #e2e8f0' }}>{t('userAnalytics.followers')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.top_followed.map((user, index) => (
                      <TableRow key={user.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell sx={{ py: 1.5 }}>
                          {index < 3 ? (
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: index === 0 ? '#fef3c7' : index === 1 ? '#f1f5f9' : '#fed7aa',
                                color: index === 0 ? '#d97706' : index === 1 ? '#64748b' : '#c2410c',
                                fontWeight: 700,
                                fontSize: 12,
                                borderRadius: 1,
                              }}
                            >
                              {index + 1}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">{index + 1}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#7c3aed', fontSize: 12 }}>
                              {user.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.5 }}>
                          <Typography variant="body2" fontWeight={600} color="#7c3aed">{user.followers_count}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Cohort Analysis */}
        {retentionData?.cohorts && retentionData.cohorts.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                  {t('userAnalytics.cohorts')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Analyse de la rétention par cohorte mensuelle
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>{t('userAnalytics.cohortMonth')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>{t('userAnalytics.joined')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>{t('userAnalytics.stillActive')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, borderBottom: '2px solid #e2e8f0' }}>{t('userAnalytics.rate')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {retentionData.cohorts.slice(0, 6).map((cohort) => {
                        const rate = Number(cohort.retention_rate);
                        return (
                          <TableRow key={cohort.cohort_month} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" fontWeight={500}>{cohort.cohort_month}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">{cohort.users_joined}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">{cohort.still_active}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  bgcolor: rate >= 70 ? '#dcfce7' : rate >= 40 ? '#fef3c7' : '#fef2f2',
                                  color: rate >= 70 ? '#16a34a' : rate >= 40 ? '#d97706' : '#dc2626',
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {rate.toFixed(1)}%
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default UserAnalyticsTab;
